<template>
  <NavBar/>
  <div class="cv-wrapper">
    <div id="cv-printable-area" class="cv-container">
      <div class="cv-layout">
        <!-- Sidebar gauche -->
        <aside class="cv-sidebar">
          <div class="photo-container">
            <div class="profile-photo-sidebar">
              <img src="/PP.png" alt="Photo de profil">
            </div>
          </div>

          <section class="sidebar-section">
            <h2>{{ $t('cv.personalInfo') }}</h2>

            <div class="info-item">
              <i class="mdi mdi-email"></i>
              <span class="info-label">{{ $t('cv.email') }}</span>
            </div>
            <p class="info-value">raphael.girardy@outlook.com</p>

            <div class="info-item">
              <i class="mdi mdi-map-marker"></i>
              <span class="info-label">{{ $t('cv.address') }}</span>
            </div>
            <p class="info-value">{{ $t('cv.addressValue') }}</p>

            <div class="info-item">
              <i class="mdi mdi-phone"></i>
              <span class="info-label">{{ $t('cv.phone') }}</span>
            </div>
            <p class="info-value">{{ $t('cv.phoneValue') }}</p>

            <div class="info-item">
              <i class="mdi mdi-calendar"></i>
              <span class="info-label">{{ $t('cv.birthdate') }}</span>
            </div>
            <p class="info-value">{{ $t('cv.birthdateValue') }}</p>

            <div class="info-item">
              <i class="mdi mdi-flag"></i>
              <span class="info-label">{{ $t('cv.nationality') }}</span>
            </div>
            <p class="info-value">{{ $t('cv.nationalityValue') }}</p>

            <div class="info-item">
              <i class="mdi mdi-car"></i>
              <span class="info-label">{{ $t('cv.license') }}</span>
            </div>
            <p class="info-value">{{ $t('cv.licenseValue') }}</p>
          </section>

          <section class="sidebar-section">
            <h2>{{ $t('cv.skillsTitle') }}</h2>

            <div class="skill-category">
              <h4>{{ $t('cv.skillBackend') }}</h4>
              <ul class="skills-list">
                <li>Java, Spring Boot, WebFlux</li>
                <li>Node.js, GraphQL, REST</li>
                <li>PostgreSQL, Redis, MongoDB</li>
                <li>Design Patterns, SOLID</li>
              </ul>
            </div>

            <div class="skill-category">
              <h4>{{ $t('cv.skillFrontend') }}</h4>
              <ul class="skills-list">
                <li>Vue.js, TypeScript</li>
              </ul>
            </div>

            <div class="skill-category">
              <h4>{{ $t('cv.skillDevOps') }}</h4>
              <ul class="skills-list">
                <li>Docker, Kubernetes (K3s)</li>
                <li>GitLab CI/CD, ArgoCD</li>
                <li>Proxmox, Linux</li>
                <li>Prometheus, Grafana</li>
              </ul>
            </div>

            <div class="skill-category">
              <h4>{{ $t('cv.skillTools') }}</h4>
              <ul class="skills-list">
                <li>Git, GitHub, GitLab</li>
                <li>C, C++, Python</li>
              </ul>
            </div>
          </section>

          <section class="sidebar-section">
            <h2>{{ $t('cv.languagesTitle') }}</h2>
            <div class="language-item">
              <span class="language-name">{{ $t('cv.langFrench') }}</span>
              <span class="language-level">{{ $t('cv.langFrenchLevel') }}</span>
            </div>
            <div class="language-item">
              <span class="language-name">{{ $t('cv.langEnglish') }}</span>
              <span class="language-level">{{ $t('cv.langEnglishLevel') }}</span>
            </div>
            <div class="language-item">
              <span class="language-name">{{ $t('cv.langSpanish') }}</span>
              <span class="language-level">{{ $t('cv.langSpanishLevel') }}</span>
            </div>
          </section>

          <!-- Centres d'intérêt - Visible uniquement à l'impression dans la sidebar -->
          <section class="sidebar-section print-only-section">
            <h2>{{ $t('cv.aboutInterestsTitle') }}</h2>
            <div class="interests-grid">
              <div v-for="(interest, index) in interestsList" :key="index" class="interest-item">
                <span v-html="interest.trim()"></span>
              </div>
            </div>
          </section>
        </aside>

        <!-- Contenu principal -->
        <main class="cv-main">
          <header class="cv-header">
            <div class="header-content">
              <h1>{{ $t('cv.name') }}</h1>
              <p class="subtitle">{{ $t('cv.subtitle') }}</p>
              <p class="motivation">{{ $t('cv.motivation') }}</p>
              <p class="objective">{{ $t('cv.objective') }}</p>
              <div class="links">
                <span>{{ $t('cv.portfolioLabel') }} <a href="https://raphael-girardy.fr" target="_blank" @click="posthog.capture('cv_portfolio_link_clicked')">raphael-girardy.fr</a></span>
              </div>
            </div>
          </header>

          <section class="main-section">
            <h2>{{ $t('cv.experiencesTitle') }}</h2>

            <div class="experience-item">
              <h3>{{ $t('cv.exp1Title') }}</h3>
              <p class="company">{{ $t('cv.exp1Company') }}</p>
              <p class="period">{{ $t('cv.exp1Period') }}</p>
              <ul>
                <li>{{ $t('cv.exp1Desc1') }}</li>
                <li>{{ $t('cv.exp1Desc2') }}</li>
              </ul>
            </div>

            <div class="experience-item">
              <h3>{{ $t('cv.exp2Title') }}</h3>
              <p class="company">{{ $t('cv.exp2Company') }}</p>
              <p class="period">{{ $t('cv.exp2Period') }}</p>
              <p class="description">{{ $t('cv.exp2Desc') }}</p>
            </div>

            <div class="experience-item">
              <h3>{{ $t('cv.exp3Title') }}</h3>
              <p class="company">{{ $t('cv.exp3Company') }}</p>
              <p class="period">{{ $t('cv.exp3Period') }}</p>
              <p class="description">{{ $t('cv.exp3Desc') }}</p>
            </div>
          </section>

          <section class="main-section">
            <h2>{{ $t('cv.projectsTitle') }}</h2>

            <div class="experience-item">
              <h3>{{ $t('cv.proj1Title') }}</h3>
              <p class="company">{{ $t('cv.proj1Subtitle') }}</p>
              <ul>
                <li>{{ $t('cv.proj1Desc1') }}</li>
                <li>{{ $t('cv.proj1Desc2') }}</li>
                <li>{{ $t('cv.proj1Desc3') }}</li>
              </ul>
            </div>
          </section>

          <section class="main-section">
            <h2>{{ $t('cv.formationsTitle') }}</h2>

            <div class="formation-item">
              <h3>{{ $t('cv.form1Title') }}</h3>
              <p class="institution">{{ $t('cv.form1Institution') }}</p>
              <p class="period">{{ $t('cv.form1Period') }}</p>
              <p class="description">{{ $t('cv.form1Desc') }}</p>
            </div>

            <div class="formation-item">
              <h3>{{ $t('cv.form2Title') }} <span class="mention">{{ $t('cv.form2Mention') }}</span></h3>
              <p class="institution">{{ $t('cv.form2Institution') }}</p>
              <p class="period">{{ $t('cv.form2Period') }}</p>
            </div>

            <div class="formation-item">
              <h3>{{ $t('cv.form3Title') }} <span class="mention">{{ $t('cv.form3Mention') }}</span></h3>
              <p class="institution">{{ $t('cv.form3Institution') }}</p>
              <p class="period">{{ $t('cv.form3Period') }}</p>
            </div>
          </section>

          <section class="main-section">
            <h2>{{ $t('cv.aboutTitle') }}</h2>

            <div class="about-subsection">
              <h3>{{ $t('cv.aboutProjectTitle') }}</h3>
              <p v-html="$t('cv.aboutProject1')"></p>
              <p v-html="$t('cv.aboutProject2')"></p>
            </div>

            <div class="about-subsection main-interests">
              <h3>{{ $t('cv.aboutInterestsTitle') }}</h3>
              <p v-html="$t('cv.aboutInterests')"></p>
            </div>
          </section>
        </main>
      </div>
    </div>

    <!-- Export Button -->
    <div class="export-section">
      <a :href="cvPdfUrl" download target="_blank" class="export-btn ln-glass-elevated" @click="trackDownload">
        <span class="btn-content">
          <i class="mdi mdi-download"></i>
          {{ $t('cv.exportButton') }}
        </span>
      </a>
    </div>
  </div>
</template>

<!-- Global print styles (non-scoped to affect NavBar) -->
<style>
@media print {
  /* Hide navigation bar in PDFs */
  .header {
    display: none !important;
  }
}
</style>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import NavBar from '@/component/common/NavBar.vue';
import {useColorStore, useLocaleStore} from '@/stores';
import { usePostHog } from '@/composables/usePostHog';

@Component({
  components: {
    NavBar
  }
})
export default class ResumeView extends Vue {
  private localeStore = useLocaleStore();
  private colorStore = useColorStore();
  private posthog = usePostHog().posthog;

  get cvPdfUrl(): string {
    const lang = this.localeStore.isFrench ? 'FR' : 'EN';
    const theme = this.colorStore.isLightTheme ? (this.localeStore.isFrench ? 'Clair' : 'Light') : (this.localeStore.isFrench ? 'Sombre' : 'Dark');

    return `/cv/CV_Raphael_Girardy_${lang}_${theme}.pdf`;
  }

  public trackDownload() {
    const lang = this.localeStore.isFrench ? 'FR' : 'EN';
    const theme = this.colorStore.isLightTheme ? 'Light' : 'Dark';
    this.posthog.capture('cv_downloaded', {
      language: lang,
      theme: theme,
      file_path: this.cvPdfUrl
    });
  }

  get interestsList(): string[] {
    const interests = this.$t('cv.aboutInterests') as string;
    return interests.split('•');
  }

  // Scroll tracking properties
  private trackedDepths = new Set<number>();
  private depthMilestones = [25, 50, 75, 90, 100];

  private handleScroll = () => {
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) return;

    const scrollPosition = window.scrollY;
    const scrollPercentage = Math.round((scrollPosition / scrollHeight) * 100);

    this.depthMilestones.forEach(depth => {
      if (scrollPercentage >= depth && !this.trackedDepths.has(depth)) {
        this.trackedDepths.add(depth);
        this.posthog.capture('page_scrolled', {
          page_name: 'resume',
          depth_percentage: depth
        });
      }
    });
  };

  mounted() {
    window.addEventListener('scroll', this.handleScroll, { passive: true });
  }

  unmounted() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
</script>

<style scoped>
.cv-wrapper {
  min-height: calc(100vh - var(--ln-nav-offset));
  padding: 3rem 2rem;
  width: 100%;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  font-family: 'TTInterfaces', sans-serif;
}

.cv-container {
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border: 1px solid var(--ln-glass-border);
  border-radius: var(--ln-radius-lg);
  box-shadow: 0 8px 32px var(--ln-glass-shadow);
  overflow: hidden;
  margin-bottom: 2rem;
}

/* Print mode */
.cv-container.print-mode {
  background: #ffffff !important;
  backdrop-filter: none !important;
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

.cv-container.print-mode .cv-sidebar {
  background: #3a4845 !important;
}

.cv-container.print-mode .cv-main {
  background: #ffffff !important;
}

.cv-container.print-mode .cv-layout {
  min-height: auto !important;
}

.cv-container.print-mode .cv-sidebar {
  padding: 1.5rem 1rem !important;
  background: #3a4845 !important;
}

.cv-container.print-mode .cv-main {
  padding: 1.5rem 2rem !important;
}

.cv-container.print-mode .sidebar-section {
  margin-bottom: 1.2rem !important;
  padding-bottom: 1rem !important;
}

.cv-container.print-mode .main-section {
  margin-bottom: 1.2rem !important;
}

.cv-container.print-mode .cv-header {
  margin-bottom: 1.2rem !important;
  padding-bottom: 1rem !important;
}

.cv-container.print-mode h1 {
  font-size: 2rem !important;
  margin-bottom: 0.3rem !important;
}

.cv-container.print-mode .subtitle {
  font-size: 0.95rem !important;
  margin-bottom: 0.6rem !important;
}

.cv-container.print-mode .motivation {
  font-size: 0.85rem !important;
  margin-bottom: 0.5rem !important;
}

.cv-container.print-mode .main-section h2 {
  font-size: 1.4rem !important;
  margin-bottom: 0.8rem !important;
}

.cv-container.print-mode .experience-item,
.cv-container.print-mode .formation-item {
  margin-bottom: 1rem !important;
}

.cv-container.print-mode .profile-photo-sidebar {
  width: 140px !important;
  height: 140px !important;
}

.cv-container.print-mode .note {
  display: none !important;
}

/* Layout à 2 colonnes */
.cv-layout {
  display: grid;
  grid-template-columns: 280px 1fr;
  min-height: calc(100vh - var(--ln-nav-offset));
}

/* Sidebar gauche */
.cv-sidebar {
  background: #3a4845;
  color: #ffffff;
  padding: 2rem 1.5rem;
}

/* Dark theme adaptation */
[data-theme="dark"] .cv-sidebar {
  background: #2d3a38;
}

.photo-container {
  text-align: center;
  margin-bottom: 2rem;
}

.profile-photo-sidebar {
  width: 160px;
  height: 160px;
  background: #e0e0e0;
  border-radius: 50%;
  margin: 0 auto;
  overflow: hidden;
  border: 4px solid #ffffff;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.profile-photo-sidebar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sidebar-section {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.3);
}

.sidebar-section:last-child {
  border-bottom: none;
}

.sidebar-section h2 {
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: #ffffff;
  background: rgba(255, 255, 255, 0.1);
  padding: 0.5rem;
  text-align: center;
  border-radius: 4px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  margin-bottom: 0.3rem;
  color: var(--ln-teal-light);
  font-weight: 600;
}

.info-item i {
  font-size: 1.2rem;
}

.info-label {
  font-size: 0.95rem;
}

.info-value {
  font-size: 0.9rem;
  color: #e0e0e0;
  margin: 0 0 0 1.7rem;
  line-height: 1.4;
}

.skills-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.skill-category {
  margin-bottom: 1rem;
}

.skill-category h4 {
  font-size: 0.95rem;
  font-weight: 600;
  color: #f0b080;
  margin-bottom: 0.5rem;
  margin-top: 0;
}

.skills-list li {
  font-size: 0.9rem;
  color: #e0e0e0;
  margin-bottom: 0.6rem;
  padding-left: 1.2rem;
  position: relative;
}

.skills-list li::before {
  content: '●';
  position: absolute;
  left: 0;
  color: var(--ln-teal-light);
}

.language-item {
  display: flex;
  flex-direction: column;
  margin-bottom: 0.8rem;
}

.language-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--ln-teal-light);
  margin-bottom: 0.2rem;
}

.language-level {
  font-size: 0.85rem;
  color: #e0e0e0;
}

/* Contenu principal */
.cv-main {
  background: var(--ln-glass-base);
  padding: 2rem 2.5rem;
}

/* Dark theme adaptation */
[data-theme="dark"] .cv-main {
  background: rgba(45, 42, 38, 0.95);
  color: var(--ln-text-primary);
}

.cv-header {
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 3px solid #e0ddd8;
}

[data-theme="dark"] .cv-header {
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.header-content {
  width: 100%;
}

.cv-header h1 {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2a2420;
  margin-bottom: 0.5rem;
}

[data-theme="dark"] .cv-header h1 {
  color: var(--ln-text-primary);
}

.cv-header .subtitle {
  font-size: 1.1rem;
  color: var(--ln-teal-deep);
  font-weight: 600;
  margin-bottom: 1rem;
  line-height: 1.4;
}

.motivation {
  font-size: 0.95rem;
  color: var(--ln-text-primary);
  line-height: 1.6;
  margin-bottom: 0.4rem;
  text-align: justify;
}

.objective {
  font-size: 0.95rem;
  color: var(--ln-text-primary);
  line-height: 1.6;
  margin-bottom: 0.8rem;
  text-align: justify;
}

.links {
  font-size: 0.9rem;
  color: #5a5450;
}

[data-theme="dark"] .links {
  color: #d0ccc8;
}

.links a {
  color: var(--ln-teal-deep);
  text-decoration: none;
  font-weight: 600;
}

/* Sections principales */
.main-section {
  margin-bottom: 2rem;
}

.main-section h2 {
  font-size: 1.8rem;
  font-weight: 700;
  color: #2a2420;
  margin-bottom: 1.2rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #d0ccc8;
}

[data-theme="dark"] .main-section h2 {
  color: var(--ln-text-primary);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

/* Expériences */
.experience-item,
.formation-item {
  margin-bottom: 1.5rem;
  padding-left: 1.2rem;
  border-left: 3px solid var(--ln-teal-deep);
}

.experience-item h3,
.formation-item h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--ln-teal-deep);
  margin-bottom: 0.3rem;
}

.company,
.institution {
  font-size: 1rem;
  color: #2a2420;
  font-weight: 500;
  font-style: italic;
  margin-bottom: 0.2rem;
}

[data-theme="dark"] .company,
[data-theme="dark"] .institution {
  color: var(--ln-text-secondary);
}

.period {
  font-size: 0.9rem;
  color: #7a7470;
  margin-bottom: 0.5rem;
}

[data-theme="dark"] .period {
  color: #b0aca8;
}

.description {
  font-size: 0.95rem;
  color: #5a5450;
  line-height: 1.6;
  text-align: justify;
}

[data-theme="dark"] .description {
  color: #d0ccc8;
}

.mention {
  font-size: 0.9rem;
  color: var(--ln-terracotta-subtle);
  font-weight: 600;
}

.experience-item ul,
.formation-item ul {
  list-style: none;
  padding: 0;
  margin: 0.5rem 0 0 0;
}

.experience-item ul li {
  font-size: 0.9rem;
  color: #5a5450;
  margin-bottom: 0.4rem;
  padding-left: 1.2rem;
  position: relative;
  line-height: 1.5;
}

[data-theme="dark"] .experience-item ul li {
  color: #d0ccc8;
}

.experience-item ul li::before {
  content: '▸';
  position: absolute;
  left: 0;
  color: var(--ln-teal-deep);
  font-weight: bold;
}

/* A propos */
.about-subsection {
  margin-bottom: 1.2rem;
}

.about-subsection h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--ln-teal-deep);
  margin-bottom: 0.5rem;
}

.about-subsection p {
  font-size: 0.95rem;
  color: #5a5450;
  line-height: 1.7;
  text-align: justify;
}

[data-theme="dark"] .about-subsection p {
  color: #d0ccc8;
}

.about-subsection p strong {
  color: var(--ln-teal-deep);
  font-weight: 600;
}

.note {
  font-size: 0.85rem;
  color: #7a7470;
  font-style: italic;
  margin-top: 0.5rem;
}

/* Print toggle classes */
.print-only-section {
  display: none;
}

.interests-sidebar {
  font-size: 0.9rem;
  color: #e0e0e0;
  line-height: 1.6;
}

/* Export Section */
.export-section {
  text-align: center;
  margin-top: 2rem;
}

.export-btn {
  padding: 1rem 2rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--ln-text-primary);
  border: 1px solid var(--ln-glass-border);
  border-radius: var(--ln-radius-md);
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  position: relative;
  overflow: hidden;
}

.export-btn::before {
  content: '';
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, var(--ln-teal-deep), var(--ln-teal-light));
  transition: top var(--ln-transition-slow) var(--ln-ease-out);
  z-index: 0;
}

.export-btn:hover::before {
  top: 0;
}

.export-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 32px var(--ln-glass-shadow-elevated);
  border-color: var(--ln-glass-border-strong);
}

.export-btn:hover .btn-content {
  color: #ffffff !important;
}

.btn-content {
  position: relative;
  z-index: 2; /* Higher than ::before */
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: var(--ln-text-primary);
  transition: color var(--ln-transition-normal) ease;
}

/* Responsive */
@media (max-width: 900px) {
  .cv-wrapper {
    margin-top: 2rem;
  }

  .cv-layout {
    grid-template-columns: 1fr;
  }

  .cv-sidebar {
    border-radius: var(--ln-radius-lg) var(--ln-radius-lg) 0 0;
  }

  .cv-main {
    border-radius: 0 0 var(--ln-radius-lg) var(--ln-radius-lg);
  }
}

@media (max-width: 768px) {
  .cv-wrapper {
    padding: 1rem;
  }

  .cv-main {
    padding: 1.5rem;
  }

  .cv-header h1 {
    font-size: 2rem;
  }
}

/* Print styles for PDF generation */
@media print {
  @page {
    margin: 0;
    size: auto;
  }

  /* Global reset for print */
  #app {
    margin: 0 !important;
    padding: 0 !important;
  }

  body {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    background: var(--ln-bg-gradient-start) !important; /* Force theme background */
  }

  /* Hide navigation and export button */
  .header,
  .export-section {
    display: none !important;
  }

  .cv-wrapper {
    padding: 0 !important;
    margin: 0 !important;
    max-width: 100% !important;
    width: 100% !important;
  }

  .cv-container {
    box-shadow: none !important;
    border: none !important;
    border-radius: 0 !important;
    margin: 0 !important;
    background: var(--ln-glass-base) !important;
    overflow: hidden !important; /* Prevent bleed issues */
  }

  /* Compact layout */
  .cv-layout {
    display: grid !important;
    grid-template-columns: 240px 1fr !important;
    min-height: 100vh;
    height: auto !important;
    border-radius: 0 !important;
  }

  /* Sidebar optimizations */
  .cv-sidebar {
    padding: 1rem !important;
    background: #3a4845 !important;
    color: white !important;
    -webkit-print-color-adjust: exact !important;
    height: 100%;
    border-radius: 0 !important;
  }

  .photo-container {
    margin-bottom: 10px !important;
  }

  .profile-photo-sidebar {
    width: 100px !important; /* Smaller photo */
    height: 100px !important;
    border-width: 2px !important;
  }

  .sidebar-section {
    margin-bottom: 10px !important;
    padding-bottom: 5px !important;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2) !important;
  }

  .sidebar-section h2 {
    font-size: 10pt !important;
    margin-bottom: 5px !important;
    padding: 2px !important;
  }

  .info-item {
    margin-top: 5px !important;
    margin-bottom: 0 !important;
    gap: 4px !important;
  }

  .info-label {
    font-size: 8pt !important;
  }

  .info-value, .skills-list li, .language-level {
    font-size: 8pt !important;
    margin-bottom: 2px !important;
    line-height: 1.2 !important;
  }

  .skill-category {
    margin-bottom: 5px !important;
  }

  .skill-category h4 {
    font-size: 9pt !important;
    margin-bottom: 2px !important;
  }

  /* Main content optimizations */
  .cv-main {
    padding: 1.5rem !important;
    background: var(--ln-glass-base) !important;
    color: var(--ln-text-primary) !important;
    border-radius: 0 !important;
  }

  .cv-header {
    margin-bottom: 10px !important;
    padding-bottom: 5px !important;
    border-bottom: 2px solid #ccc !important;
  }

  .cv-header h1 {
    font-size: 18pt !important;
    margin-bottom: 2px !important;
    color: var(--ln-text-primary) !important;
  }

  .cv-header .subtitle {
    font-size: 10pt !important;
    margin-bottom: 5px !important;
    color: #81e6d9 !important;
  }

  .motivation {
    font-size: 8pt !important;
    margin-bottom: 5px !important;
    line-height: 1.2 !important;
  }

  .objective {
    margin-bottom: 5px !important;
    line-height: 1.2 !important;
  }

  .links, .objective {
    font-size: 7pt !important;
  }

  .links span {
    font-size: 7pt !important;
    color: var(--ln-text-primary) !important;
  }

  .links a {
    color: var(--ln-text-primary) !important;
  }

  .main-section {
    margin-bottom: 10px !important;
  }

  .main-section h2 {
    font-size: 13pt !important;
    margin-bottom: 8px !important;
    padding-bottom: 4px !important;
    border-bottom: 1px solid var(--ln-glass-border) !important;
    color: var(--ln-teal-deep) !important;
  }

  .experience-item, .formation-item {
    margin-bottom: 8px !important;
    padding-left: 8px !important;
    border-left-width: 2px !important;
    border-left-color: var(--ln-teal-deep) !important;
  }

  .experience-item h3, .formation-item h3 {
    font-size: 10pt !important;
    margin-bottom: 1px !important;
  }

  .company, .institution, .period {
    font-size: 9pt !important;
    margin-bottom: 1px !important;
  }

  .description, .experience-item ul li, .about-subsection p {
    font-size: 9pt !important;
    margin-bottom: 2px !important;
    line-height: 1.25 !important;
  }

  /* Ensure backgrounds render */
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  /* Force text colors for readability on paper */
  p, span, li, h1, h2, h3, h4 {
    color: black;
  }

  /* Override specific elements to keep some color but dark enough */
  .cv-sidebar, .cv-sidebar p, .cv-sidebar span, .cv-sidebar li, .cv-sidebar h2 {
    color: white !important;
  }

  .cv-sidebar h4 {
    color: #f0b080 !important;
  }

  .info-item {
    color: #81e6d9 !important;
  }

  /* Affichage des centres d'intérêt dans la sidebar pour le PDF */
  .print-only-section {
    display: block !important;
    margin-top: 10px !important;
    padding-top: 5px !important;
  }

  .interests-grid {
    display: grid !important;
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 8px !important;
    padding: 5px 0 !important;
  }

  .interest-item {
    font-size: 7pt !important;
    color: white !important;
    line-height: 1.2 !important;
    position: relative !important;
    padding-left: 12px !important;
  }

  .interest-item::before {
    content: '●' !important;
    position: absolute !important;
    left: 0 !important;
    color: #81e6d9 !important;
    font-size: 6pt !important;
    top: 1px !important;
  }

  .main-interests {
    display: none !important;
  }
}

</style>
